import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getDateLocale } from '@/utils/dateLocale';
import { CalendarPlus, Check, Clock, Hourglass, Mail, Navigation, Pencil, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Service } from '@/types/booking';
import type { PublicOrganization } from '../../booking-page/types';
import type { NormalizedSlot } from '@/utils/normalizeSlot';

type Outcome = 'confirmed' | 'pending' | 'waitlist';

interface BookingConfirmationProps {
  bookingConfirmation: any;
  organization: PublicOrganization;
  selectedService: Service | null;
  selectedSlot: NormalizedSlot | null;
  /** "Wed 23 Sep · 14:40 – 15:30"; built by the wizard. */
  whenLabel?: string | null;
  priceLabel?: string;
  personName?: string;
  generateGoogleCalendarUrl: () => string;
  clearBookingStorage: () => void;
}

// Step 3: confirmed, pending (the business confirms by hand) or waitlisted.
export function BookingConfirmation({
  bookingConfirmation,
  organization,
  selectedService,
  selectedSlot,
  whenLabel,
  priceLabel,
  personName,
  generateGoogleCalendarUrl,
  clearBookingStorage,
}: BookingConfirmationProps) {
  const { t } = useTranslation(['publicBooking', 'bookingPage']);
  const page = organization.booking_page;
  const outcome: Outcome = bookingConfirmation?.waitlisted === true
    ? 'waitlist'
    : bookingConfirmation.status === 'confirmed' ? 'confirmed' : 'pending';

  const when = whenLabel
    ?? (selectedSlot ? format(parseISO(selectedSlot.startTime), 'EEE d MMM · HH:mm', { locale: getDateLocale() }) : null);
  const duration = selectedService?.duration_minutes ?? selectedService?.duration;
  const where = page?.location?.address ?? (page?.show_online ? t('bookingPage:steps.done.online') : null);
  const rows: [string, string | null | undefined][] = [
    [
      outcome === 'waitlist' ? t('bookingPage:steps.done.session') : t('bookingPage:steps.summary.service'),
      [selectedService?.name, duration ? t('bookingPage:steps.minutes', { count: duration }) : null].filter(Boolean).join(' · '),
    ],
    [t('bookingPage:steps.with'), personName],
    [t('bookingPage:steps.summary.when'), when],
    ...(outcome === 'waitlist' ? [] : [[t('bookingPage:steps.done.where'), where] as [string, string | null]]),
    ...(outcome === 'waitlist' ? [] : [[t('bookingPage:steps.done.price'), priceLabel] as [string, string | undefined]]),
  ];

  const email = bookingConfirmation.customer_email || bookingConfirmation.entry?.customer_email;
  const cancellationHours = page?.cancellation_hours;
  const manageUrl = bookingConfirmation.guest_token ? `/guest/bookings/${bookingConfirmation.guest_token}` : null;
  const waitlistUrl = bookingConfirmation.entry?.guest_token ? `/guest/waitlist/${bookingConfirmation.entry.guest_token}` : null;

  const bookAnother = () => {
    clearBookingStorage();
    const url = new URL(window.location.href);
    url.searchParams.delete('step');
    window.history.replaceState({}, '', url.toString());
    window.location.reload();
  };

  const hero = {
    confirmed: {
      title: t('bookingPage:steps.done.confirmedTitle'),
      body: t('bookingPage:steps.done.confirmedBody', { name: organization.name }),
    },
    pending: {
      title: t('bookingPage:steps.done.pendingTitle'),
      body: t('bookingPage:steps.done.pendingBody', { name: organization.name }),
    },
    waitlist: {
      title: t('publicBooking:confirmation.waitlist.heading', "You're on the waitlist"),
      body: t('publicBooking:confirmation.waitlist.emailNote', "We'll email you when a spot opens up."),
    },
  }[outcome];

  const position: number | undefined = bookingConfirmation.position;
  const ahead = Math.max(0, (position ?? 1) - 1);

  const details = (
    <div className="flex flex-col gap-1.5 rounded-[18px] border border-line bg-surface px-5 py-5 sm:px-[22px]" data-testid="booking-details">
      <div className="flex pb-1.5">
        <StatusBadge outcome={outcome} />
      </div>
      {bookingConfirmation.booking_code && (
        <div className="flex justify-between gap-4 py-3 text-[14px]">
          <span className="text-soft">{t('bookingPage:steps.done.code')}</span>
          <span className="font-mono font-semibold tracking-[0.04em] text-ink" data-testid="booking-code">{bookingConfirmation.booking_code}</span>
        </div>
      )}
      {rows.filter(([, v]) => !!v).map(([k, v], i) => (
        <div key={k} className={cn('flex justify-between gap-4 py-3 text-[14px]', (i > 0 || bookingConfirmation.booking_code) && 'border-t border-line')}>
          <span className="text-soft">{k}</span>
          <span className="text-right font-medium text-ink">{v}</span>
        </div>
      ))}
    </div>
  );

  const emailNote = (
    <div className="flex items-start gap-2.5 rounded-xl bg-surface-2 px-3.5 py-3 text-[13px] leading-normal text-soft">
      <Mail className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {email ? t('bookingPage:steps.done.emailSent', { email }) : t('publicBooking:confirmation.checkEmail')}{' '}
        {t('publicBooking:confirmation.checkSpam')}
      </span>
    </div>
  );

  const contact = (page?.phone || page?.email || page?.location?.directions_url) && (
    <div className="flex flex-col gap-3 rounded-[18px] border border-line bg-surface px-5 py-[18px] sm:px-[22px]" data-testid="done-contact">
      <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-soft">{organization.name}</span>
      {page.phone && (
        <a href={`tel:${page.phone.replace(/\s+/g, '')}`} className="inline-flex min-h-[32px] items-center gap-2.5 text-[14px] text-ink">
          <Phone className="h-4 w-4 text-soft" />{page.phone}
        </a>
      )}
      {page.email && (
        <a href={`mailto:${page.email}`} className="inline-flex min-h-[32px] items-center gap-2.5 break-all text-[14px] text-ink">
          <Mail className="h-4 w-4 shrink-0 text-soft" />{page.email}
        </a>
      )}
      {page.location?.directions_url && (
        <a href={page.location.directions_url} target="_blank" rel="noreferrer" className="inline-flex min-h-[32px] items-center gap-2.5 text-[14px] font-medium text-brand-500">
          <Navigation className="h-4 w-4" />{t('bookingPage:location.directions')}
        </a>
      )}
    </div>
  );

  const changeNote = outcome !== 'waitlist' && manageUrl && cancellationHours != null && (
    <div className="flex flex-col gap-1.5 rounded-[18px] bg-brand-50 px-5 py-[18px] text-brand-700" data-testid="change-note">
      <span className="text-[15px] font-semibold">{t('bookingPage:steps.done.changeTitle')}</span>
      <span className="text-[14px] leading-normal">{t('bookingPage:steps.done.changeBody', { count: cancellationHours })}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="booking-done" data-outcome={outcome}>
      <div className="flex flex-col items-center gap-3 pb-1 pt-3 text-center sm:pb-2 sm:pt-5">
        <span className={cn('inline-flex h-14 w-14 items-center justify-center rounded-pill sm:h-16 sm:w-16', outcome === 'confirmed' ? 'bg-success-soft text-success-text' : 'bg-brand-50 text-brand-700')}>
          {outcome === 'confirmed' ? <Check className="h-7 w-7" strokeWidth={2.25} /> : outcome === 'pending' ? <Clock className="h-7 w-7" /> : <Hourglass className="h-7 w-7" />}
        </span>
        <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">{hero.title}</h1>
        <p className="max-w-[480px] text-[15px] leading-normal text-soft sm:text-[16px]">{hero.body}</p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-3.5">
          {outcome === 'waitlist' && position != null && (
            <div className="flex items-center gap-4 rounded-[18px] border border-line bg-surface px-5 py-[18px]" data-testid="waitlist-position">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 font-display text-[26px] font-semibold text-brand-700">{position}</span>
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-ink">
                  {t('publicBooking:confirmation.waitlist.positionLine', "You're number {{position}} on the waitlist.", { position })}
                </span>
                <span className="text-[14px] text-soft">
                  {ahead === 0
                    ? t('publicBooking:confirmation.waitlist.aheadLineNone', "You're first in line.")
                    : t('publicBooking:confirmation.waitlist.aheadLine', '{{count}} ahead of you.', { count: ahead })}
                </span>
              </div>
            </div>
          )}

          {details}

          {outcome === 'waitlist' ? (
            waitlistUrl && (
              <Button asChild className="h-[52px] w-full text-[15px] font-semibold">
                <a href={waitlistUrl}>{t('publicBooking:confirmation.waitlist.viewStatusCta', 'View waitlist status')}</a>
              </Button>
            )
          ) : (
            <>
              <div className={cn('grid gap-2.5', manageUrl && 'sm:grid-cols-2')}>
                {/* Also on a booking still waiting to be accepted: the time is
                    held, so the customer wants it in their calendar either way. */}
                <Button asChild className="h-[52px] text-[15px] font-semibold">
                  <a href={generateGoogleCalendarUrl()} target="_blank" rel="noopener noreferrer">
                    <CalendarPlus className="h-5 w-5" />
                    {t('bookingPage:steps.done.addToCalendar')}
                  </a>
                </Button>
                {manageUrl && (
                  <Button asChild variant="outline" className="h-[52px] text-[15px] font-semibold">
                    <a href={manageUrl}>
                      <Pencil className="h-[18px] w-[18px]" />
                      {t('bookingPage:steps.done.manage')}
                    </a>
                  </Button>
                )}
              </div>
              <button type="button" onClick={bookAnother} className="self-center p-3 text-[14px] font-medium text-brand-500 hover:text-brand-700">
                {t('bookingPage:steps.done.bookAnother')}
              </button>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3.5">
          {changeNote}
          {contact}
          {emailNote}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ outcome }: { outcome: Outcome }) {
  const { t } = useTranslation(['publicBooking', 'bookingPage']);
  if (outcome === 'confirmed') {
    return (
      <span className="inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-pill bg-success-soft px-2.5 text-[12px] font-semibold text-success-text" data-testid="status-badge">
        <span className="h-1.5 w-1.5 rounded-pill bg-success" />
        {t('publicBooking:confirmation.statusConfirmed')}
      </span>
    );
  }
  if (outcome === 'pending') {
    return (
      <span className="inline-flex h-[26px] items-center whitespace-nowrap rounded-pill bg-warn-soft px-2.5 text-[12px] font-semibold text-warn-text" data-testid="status-badge">
        {t('publicBooking:confirmation.statusPending')}
      </span>
    );
  }
  return (
    <span className="inline-flex h-[26px] items-center whitespace-nowrap rounded-pill bg-brand-100 px-2.5 text-[12px] font-semibold text-brand-700" data-testid="status-badge">
      {t('bookingPage:steps.done.onWaitlist')}
    </span>
  );
}
